// src/encounter/phrase-chart-card.tsx
//
// THE VALUE CARD of a smart phrase's chart piece: what the piece quoted from the chart, frozen at
// insertion, drawn as tiles — one per value — instead of one long line of text. Generic: it draws
// ANY chart query from its snapshot (a catalogue lab set, one lab test, vitals, allergies, active
// problems, medications); it knows nothing about any panel.
//
//   · a LAB value  → label, value + unit (the only coloured element, from the LAB'S OWN flag),
//                    collection date · laboratory · the flag in words, the printed range;
//   · a VITAL      → glyph, value + unit, label · date, and its trend (sparkline + reading count)
//                    when the editor read the history;
//   · anything else (allergies, problems, medications, or a vital quoted before card fields existed)
//                  → its line of text, as the note says it.
//
// PORTED from the clinician demo (Scripsteam/scrips-clinician-demo: PhraseNote.tsx ChartBlock,
// Tiles.tsx KeyLabTile / VitalTile), composed from existing Signal DS primitives (Button, Icon,
// Sparkline, VitalIcon, VITAL_TILE_KIND) with token utilities — no new DS release. It graduates into
// Signal DS as its own component after the Omics session (follow-up, PROD-2948).
//
// Clinical display rules, each a difference from the demo where the demo could compute:
//   1. NEVER COMPUTED. The flag is the lab's interpretation verbatim; the DS-020 direction glyph is
//      read from the flag CODE only (exact H / HH up, L / LL down — the desk ruling the DS
//      ObservationReadout follows). No range or value is ever compared.
//   2. COLLECTION DATE. A tile's date is when the specimen was collected; with none it says
//      "Reported …" in words (and the resolver never lets an undated result outrank a dated one).
//   3. A missing flag reads "no lab flag", never "within lab range".
//   4. NEVER JOINED. One tile = one observation from one report.
// Everything here is the clinician's DRAFT: nothing reaches the chart until the note is signed.

import { type ReactNode } from 'react';
import { Button, Sparkline, VitalIcon, VITAL_TILE_KIND } from '@scripsteam/scrips-signal-ds';
import { AlertTriangle } from 'lucide-react';
import { chartName, isLabQuery, wallClockDate, wallClockStamp, type ChartPiece, type ChartResult } from './lib/note-pieces';
import { carriedFlagGlyph, labMeta, labMetaParts, readCarriedFlag, type FlagSeverity } from './lib/chart-card-model';

const VALUE_TONE: Record<FlagSeverity, string> = {
  none: 'text-text-primary',
  mild: 'text-yellow-700',
  critical: 'text-red-700',
};

/** The demo's label step: 12px bold caps, section-heading tracking. */
const LABEL_CAPS = 'text-size-eyebrow font-bold uppercase leading-none tracking-section-heading';
const TILE = 'min-w-0 rounded-lg border border-border-default bg-surface-card px-3 py-2.5 text-left shadow-2';
const META = 'whitespace-nowrap text-size-eyebrow text-text-secondary';
const LINE = 'text-size-sm text-text-secondary';

function ChartLabTile({ r }: { r: ChartResult }) {
  const { severity } = readCarriedFlag(r.flag);
  const glyph = carriedFlagGlyph(r.flag);
  const label = r.label ?? r.display;
  return (
    <div
      data-chart-lab={r.code ?? r.sourceId ?? label}
      data-flag-severity={severity === 'none' ? undefined : severity}
      title={`${label} · ${labMeta(r)}`}
      className={`${TILE} flex h-full flex-col gap-1`}
    >
      {/* The analyte's full name, wrapped — never truncated: "(calc)" or "direct" is clinical meaning. */}
      <span data-lab-label className={`${LABEL_CAPS} break-words leading-tight text-text-secondary`}>
        {label}
      </span>
      <span className="whitespace-nowrap leading-tight">
        <span data-lab-value className={`text-size-md font-semibold tabular-nums ${VALUE_TONE[severity]}`}>
          {glyph ? `${glyph} ` : ''}
          {r.value}
        </span>
        {r.unit && <span className="ml-1 text-size-eyebrow text-text-secondary">{r.unit}</span>}
      </span>
      <span data-lab-meta className="text-size-eyebrow leading-snug tabular-nums text-text-secondary">
        {labMetaParts(r).map((part, i) => (
          <span key={`${i}:${part}`}>
            {i > 0 && ' · '}
            <span data-lab-meta-part className="whitespace-nowrap">
              {part}
            </span>
          </span>
        ))}
      </span>
      {r.range && (
        <span data-lab-range className="text-size-eyebrow leading-snug text-text-secondary">
          Lab range {r.range}
        </span>
      )}
    </div>
  );
}

function ChartVitalTile({ r }: { r: ChartResult }) {
  const kind = r.kind ? VITAL_TILE_KIND[r.kind] : undefined;
  const history = r.history ?? [];
  const sub = [r.label, r.measuredAt ? (wallClockDate(r.measuredAt) ?? r.measuredAt) : 'date not recorded', r.flag]
    .filter(Boolean)
    .join(' · ');
  return (
    <div data-chart-vital={r.kind} title={sub} className={`${TILE} @container flex`}>
      {/* Narrow: value, then date, then the trend. Wide: the trend beside the value (the demo's
          container queries, unchanged). */}
      <span className="flex w-full min-w-0 gap-1 @max-xs:flex-col @xs:items-center @xs:justify-between @xs:gap-3">
        <span className="flex min-w-0 flex-col gap-1">
          <span className="flex min-w-0 items-center gap-2">
            {kind && <VitalIcon kind={kind} className="h-5 w-5 shrink-0" />}
            <span className="min-w-0 text-size-base font-semibold leading-tight tabular-nums text-text-primary">
              <span data-vital-value className="whitespace-nowrap">
                {r.value}
              </span>
              {r.unit && <span className="ml-1 whitespace-nowrap text-size-eyebrow font-normal text-text-secondary">{r.unit}</span>}
            </span>
          </span>
          <span data-vital-meta className="text-size-eyebrow leading-tight text-text-secondary">
            {sub}
          </span>
        </span>
        {history.length > 0 && (
          <span data-vital-trend className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 @xs:flex-col @xs:flex-nowrap @xs:items-end @xs:gap-1">
            {history.length > 1 && <Sparkline data={history} width={64} height={20} emphasizeLast />}
            <span data-vital-readings className="whitespace-nowrap text-size-eyebrow tabular-nums text-text-secondary">
              {history.length} recent reading{history.length === 1 ? '' : 's'}
            </span>
          </span>
        )}
      </span>
    </div>
  );
}

export interface ChartPieceCardProps {
  piece: ChartPiece;
  /** One line after "as of" (e.g. "Unchanged as of …", "Couldn’t re-read the chart"). */
  refreshNote?: string;
  /** Re-read the chart now. Absent → no Refresh. */
  onRefresh?: () => void;
  refreshing?: boolean;
  /** Newer values were found; the quote keeps its values until Update. */
  stale?: boolean;
  onUpdate?: () => void;
  /** Turn the piece into plain narrative. Absent → no button (the host may offer its own). */
  onMakePlainText?: () => void;
  /** Extra controls at the end of the header (the host's). */
  actions?: ReactNode;
}

/**
 * The VALUES of one chart piece — its state line and one tile per value — with NO header and NO
 * "as of" stamp: the host's header carries those once (D-45). This is what the structured-note
 * phrase occurrence (PR #611) mounts through its `renderChartPiece` hook:
 *   renderChartPiece={(piece) => <ChartPieceTiles piece={piece} />}
 */
export function ChartPieceTiles({ piece, stale, onUpdate }: Pick<ChartPieceCardProps, 'piece' | 'stale' | 'onUpdate'>) {
  const s = piece.snapshot;
  const title = chartName(piece.query, piece.label);
  const lab = isLabQuery(piece.query);
  // A named list reads in lower case ("allergies"); a lab set keeps its own casing ("CBC").
  const name = lab ? title : title.toLowerCase();
  const status = s?.status ?? 'loading';
  const results = status === 'ok' ? (s?.results ?? []) : [];
  const labs = lab ? results.filter((r) => r.value !== undefined) : [];
  const vitals = !lab ? results.filter((r) => r.kind !== undefined && r.value !== undefined) : [];
  const lines = results.filter((r) => !labs.includes(r) && !vitals.includes(r));
  return (
    <div
      data-chart-tiles={piece.query}
      data-state={status === 'ok' && stale ? 'stale' : status}
      className="@container flex basis-full flex-col gap-2"
    >
      {status === 'loading' && (
        <span data-chart-line="loading" aria-busy="true" className={LINE}>
          Not loaded from the chart yet.
        </span>
      )}
      {status === 'error' && (
        <span data-chart-line="error" className={`flex items-center gap-2 ${LINE}`}>
          <AlertTriangle className="size-4 shrink-0" aria-hidden />
          Couldn’t load {name} from the chart. Nothing was quoted.
        </span>
      )}
      {status === 'empty' && (
        <span data-chart-line="empty" className={LINE}>
          {lab ? `No ${title} results recorded in the chart.` : `No ${name} recorded in the chart.`}
        </span>
      )}
      {status === 'ok' && stale && (
        <span data-chart-line="stale" className="flex flex-wrap items-center gap-2 text-size-sm text-yellow-700">
          <AlertTriangle className="size-4 shrink-0" aria-hidden />
          The chart has newer values than this quote.
          {onUpdate && (
            <Button variant="primary" size="sm" onMouseDown={(e) => e.preventDefault()} onClick={onUpdate}>
              Update
            </Button>
          )}
        </span>
      )}

      {vitals.length > 0 && (
        <div className="grid gap-3 @sm:grid-cols-3">
          {vitals.map((r) => (
            <ChartVitalTile key={`${r.kind}-${r.sourceId ?? r.display}`} r={r} />
          ))}
        </div>
      )}
      {labs.length > 0 && (
        <div className="grid gap-3 @xs:grid-cols-2 @lg:grid-cols-3 @3xl:grid-cols-4">
          {labs.map((r) => (
            <ChartLabTile key={r.sourceId ?? r.display} r={r} />
          ))}
        </div>
      )}
      {lines.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {lines.map((r) => (
            <li key={r.sourceId ?? r.display} data-chart-text className="text-size-sm font-semibold text-text-primary">
              {r.display}
              {r.recordedBy && <span className="ml-2 text-size-eyebrow font-normal text-text-secondary">Recorded by {r.recordedBy}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The card for one chart piece: the header ("<name> · from the chart", "as of", Refresh, Make plain
 *  text) once, then its values (`ChartPieceTiles`). Any query, any number of values. */
export function ChartPieceCard({ piece, refreshNote, onRefresh, refreshing, stale, onUpdate, onMakePlainText, actions }: ChartPieceCardProps) {
  const s = piece.snapshot;
  const title = chartName(piece.query, piece.label);
  const name = isLabQuery(piece.query) ? title : title.toLowerCase();
  const status = s?.status ?? 'loading';
  // D-45: "as of 5 Oct 2026, 22:00", here and only here.
  const asOf = wallClockStamp(s?.pulledAt);
  return (
    <div
      data-chart-card={piece.query}
      data-state={status === 'ok' && stale ? 'stale' : status}
      className="@container flex basis-full flex-col gap-2 py-1"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className={`${LABEL_CAPS} whitespace-nowrap text-text-secondary`}>{title} · from the chart</span>
        {asOf && (
          <span data-as-of className={META}>
            as of {asOf}
          </span>
        )}
        {refreshNote && (
          <span data-refresh-note className={META}>
            {refreshNote}
          </span>
        )}
        {(onRefresh || onMakePlainText || actions) && (
          <span className="ml-auto flex items-center gap-1">
            {onRefresh && (
              <Button variant="tertiary" size="sm" onMouseDown={(e) => e.preventDefault()} onClick={onRefresh} disabled={refreshing} aria-label={`Refresh ${name} from the chart`}>
                {refreshing ? 'Checking…' : 'Refresh'}
              </Button>
            )}
            {onMakePlainText && (
              <Button variant="tertiary" size="sm" onMouseDown={(e) => e.preventDefault()} onClick={onMakePlainText}>
                Make plain text
              </Button>
            )}
            {actions}
          </span>
        )}
      </div>
      <ChartPieceTiles piece={piece} stale={stale} onUpdate={onUpdate} />
    </div>
  );
}
